import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, storeSession, clearSession, revokeServerSession, APIError } from '../lib/api';

interface User {
  id: string;
  email: string;
  full_name?: string;
  role: string;
  company_name?: string;
  company_logo_url?: string;
  company_phone?: string;
  company_email?: string;
  company_address?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, fullName: string, inviteToken: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * The last profile /auth/me (or login) returned, kept so the app can OPEN WITH NO SIGNAL (WT1, 2026-09-23).
 * It is one of api.ts's SESSION_KEYS, so every clearSession - logout, a refused refresh - takes it too.
 */
const CACHED_USER_KEY = 'auth_user';

const isUser = (v: unknown): v is User => {
  if (typeof v !== 'object' || v === null) return false;
  const u = v as Record<string, unknown>;
  return typeof u.id === 'string' && u.id !== '' && typeof u.email === 'string' && typeof u.role === 'string';
};

// localStorage can throw (private mode, quota, a disabled store); a cache that cannot be read is no cache.
const readCachedUser = (): User | null => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * The server ANSWERED and said no: a 4xx, the same line api.ts's refresh draws. By the time /auth/me's 401
 * reaches here request() has already tried a refresh; a 403 is a deactivated account. A 5xx is not a verdict
 * on the token, and neither is an error that never came from the server at all.
 */
const isRefusal = (err: unknown): boolean => err instanceof APIError && err.status >= 400 && err.status < 500;

const cacheUser = (user: unknown): void => {
  if (!isUser(user)) return;
  try {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
  } catch {
    // the app still works online without the cache
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let onOnline: (() => void) | null = null;

    const stopListening = () => {
      if (onOnline) window.removeEventListener('online', onOnline);
      onOnline = null;
    };

    const accept = (data: { user?: unknown }) => {
      if (!active) return;
      setUser((data?.user as User) ?? null);
      cacheUser(data?.user);
    };

    /**
     * ⛔ Only a REFUSAL clears the session. Anything else - a NetworkError (no signal), a 5xx (Railway
     *    restarting as he opens the app), a 200 that is not this API (a captive portal) - is the server NOT
     *    ANSWERING about the session, so it keeps the token, restores the cached profile when there is one,
     *    and re-checks once signal returns. Opening the installed app inside a building, or during a deploy,
     *    no longer logs him out and strands the walk-through queue.
     *    With signal already up, 'online' never fires: the app's next request IS the re-check - a refused
     *    token 401s there and api.ts refreshes, or clears and goes to /login.
     */
    const reject = (err: unknown) => {
      if (!active) return;
      if (!isRefusal(err)) {
        setUser(readCachedUser());
        if (!onOnline) {
          onOnline = () => {
            stopListening();
            api.getCurrentUser().then(accept).catch(reject);
          };
          window.addEventListener('online', onOnline);
        }
        return;
      }
      clearSession();
      setUser(null);
    };

    const token = localStorage.getItem('token');
    if (token) {
      api
        .getCurrentUser()
        .then(accept)
        .catch(reject)
        .finally(() => {
          if (active) setLoading(false);
        });
    } else {
      setUser(null);
      setLoading(false);
    }

    return () => {
      active = false;
      stopListening();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const data: any = await api.login(email, password);
    // Stores the refresh token and expiry too, not just the access token. Dropping them is
    // what made the app log you out an hour into every session - backlog B17.
    storeSession(data.session);
    cacheUser(data.user);
    setUser(data.user);
  };

  const signup = async (email: string, password: string, fullName: string, inviteToken: string) => {
    const data: any = await api.signup(email, password, fullName, inviteToken);
    // Signup doesn't return a session, user needs to login
    setUser(data.user);
  };

  const logout = () => {
    // ⛔ AUD-H5 (the 124th): end THIS session on the server too. `revokeServerSession` reads the token before it awaits, so
    //   clearing storage on the next line cannot take it away first; it never throws and never blocks the sign out.
    void revokeServerSession();
    clearSession();
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => {
      const next = prev ? { ...prev, ...updates } : null;
      // Keep the offline copy in step, or a cold start with no signal would show the old name.
      if (next) cacheUser(next);
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
